import { Component, Input, OnInit, Output, EventEmitter, OnChanges, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import moment, { unitOfTime } from 'moment';
import { BsDatepickerConfig, BsDatepickerViewMode, BsLocaleService } from 'ngx-bootstrap/datepicker';
import { Observable, ReplaySubject, combineLatest } from 'rxjs';
import { ThemeService } from '@wind-log_library/fe-component-library';
import { map, takeUntil } from 'rxjs/operators';
import { PERIOD_OPTIONS, periodInputFormats } from 'app/data-studio-redesign/services/data-analysis-helper';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { defineLocale} from 'ngx-bootstrap/chronos';
import * as locale from 'ngx-bootstrap/locale';
import { LanguageService } from 'app/shared/services/language/language.service';

@Component({
    selector: 'app-data-studio-period',
    templateUrl: './data-studio-period.component.html',
    styleUrls: ['./data-studio-period.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DataStudioPeriodComponent implements OnInit, OnChanges, OnDestroy {

  @Input() hidePeriodSelector: boolean;
  @Input() periodLabel: string;
  @Input() disabled: boolean;
  @Input() startTs: number;
  @Input() endTs: number;
  @Output() changePeriod = new EventEmitter();

  public bsConfig: Observable<Partial<BsDatepickerConfig>>;
  public startDate: Date;
  public endDate: Date;
  public selectedPeriod: [Date, Date];
  public selectedDate: Date;
  public today: Date;
  public minDate: Date;
  public periodOptions: { names: string[], values: string[] } = PERIOD_OPTIONS;
  public periodIndex: number;
  private datePickerOptions = new ReplaySubject();
  private unsubscriber = new ReplaySubject();

  public get isEndToday() {
    const now = new Date();
    return now.getDate() === this.endDate.getDate() && 
      now.getMonth() === this.endDate.getMonth() && 
      now.getFullYear() === this.endDate.getFullYear();
  }

  constructor(
    private translateService: TranslateService,
    private localeService: BsLocaleService,
    private themeService: ThemeService,
    private languageService: LanguageService
  ) { }

  public ngOnInit(): void {
    this.today = new Date();
    this.subscribeLanguageChange();
  }

  public ngOnChanges(changes): void {
    if (changes.startTs) {
      this.startDate = new Date(this.startTs);
      this.selectedPeriod = [this.startDate, this.endDate];
    }

    if (changes.endTs) {
      this.endDate = new Date(this.endTs);
      this.selectedPeriod = [this.startDate, this.endDate];
    }

    if (changes.periodLabel?.currentValue) {
      this.selectedDate = !['Week', 'Custom'].includes(this.periodLabel) && this.startDate;
      this.periodIndex = this.periodOptions.names.findIndex(item => item === this.periodLabel);
      this.setDatepickerConfig();
    }
  }

  public ngOnDestroy() {
    this.unsubscriber.next(true);
    this.unsubscriber.complete();
  }

  public changeRangeDate(event) {
    if (moment(event[0]).valueOf() !== moment(this.startDate).valueOf() ||
      moment(event[1]).valueOf() !== moment(this.endDate).valueOf()) {
        this.startDate = new Date(event[0]);
        this.endDate = new Date(event[1]);
        if (moment(this.endDate).valueOf() > moment().endOf('d').valueOf()) {
          this.endDate = new Date(moment().endOf('d').valueOf());
        }
        this.endDate = moment(this.endDate).endOf('d').toDate();
        this.changePeriod.emit({startTs: this.startDate.getTime(), endTs: this.endDate.getTime(), period: this.periodLabel});
      }
  }

  public changeDate(event) {
    if (moment(event).valueOf() !== moment(this.startDate).valueOf()) {
      const startOf = this.periodLabel.toLowerCase() as unitOfTime.StartOf;
      this.startDate = new Date(moment(event).startOf(startOf).valueOf());
      const currentPeriod = moment(event).endOf(startOf).valueOf() + 1 > moment().valueOf()
      this.endDate = new Date(currentPeriod ? moment().valueOf() : moment(event).endOf(startOf).valueOf() + 1);
      if (moment(this.endDate).valueOf() > moment().endOf('d').valueOf()) {
        this.endDate = new Date(moment().endOf('d').valueOf());
      }
      this.changePeriod.emit({startTs: this.startDate.getTime(), endTs: this.endDate.getTime(), period: this.periodLabel});
    }
  }

  public selectPeriod({option, index}) {
    this.periodLabel = option;
    this.periodIndex = index;

    if (this.periodIndex === 0) {
      this.startDate = new Date(moment(this.startDate).startOf('day').valueOf());
      this.endDate = new Date(moment(this.startDate).endOf('day').valueOf());
      this.selectedDate = this.startDate;
    }

    if (this.periodIndex === 1) {
      this.startDate = new Date(moment(this.startDate).startOf('week').valueOf());
      this.endDate = new Date(moment(this.startDate).endOf('week').valueOf());
      this.selectedPeriod = [this.startDate, this.endDate];
    }

    if (this.periodIndex === 2) {
      this.startDate = new Date(moment(this.startDate).startOf('month').valueOf());
      this.endDate = new Date(moment(this.startDate).add(1, 'month').valueOf());
      this.selectedDate = this.startDate;
    }

    if (this.periodIndex === 3) {
      this.startDate = new Date(moment(this.startDate).startOf('year').valueOf());
      this.endDate = new Date(moment(this.startDate).add(1, 'year').valueOf());
      this.selectedDate = this.startDate;
    }

    if (this.periodIndex === 4) {
      this.selectedPeriod = [this.startDate, this.endDate];
    }

    if (moment(this.endDate).valueOf() > moment().endOf('d').valueOf()) {
      this.endDate = new Date(moment().endOf('d').valueOf());
    }
    this.changePeriod.emit({startTs: this.startDate.getTime(), endTs: this.endDate.getTime(), period: this.periodLabel});
  }

  public prevPeriod() {
    if (this.periodIndex === 4) {
      this.updatePeriodCustom('subtract');
    } else {
      this.updatePeriod('subtract', this.periodOptions.values[this.periodIndex]);
    }
  }

  
  public nextPeriod() {
    if (this.periodIndex === 4) {
      this.updatePeriodCustom('add');
    } else {
      this.updatePeriod('add', this.periodOptions.values[this.periodIndex]);
    }
  }

  private updatePeriod(action, period) {
    if (this.isLastDate(period, 1) && action === 'add') {
      this.endDate = new Date();
      this.startDate = new Date(moment().startOf(period).valueOf());
    } else {
      if (moment(this.endDate).endOf(period).valueOf() === moment().endOf(period).valueOf() && action === 'subtract') {
        this.endDate = new Date(moment().endOf(period).valueOf());
      }
      this.startDate = new Date(moment(this.startDate)[action](1, period).valueOf());
      this.endDate = new Date(moment(this.endDate)[action](1, period).valueOf());
    }
    this.selectedDate = this.startDate;
    if (moment(this.endDate).valueOf() > moment().endOf(period).valueOf()) {
      this.endDate = new Date(moment().endOf(period).valueOf());
    }
    this.selectedPeriod = [this.startDate, this.endDate];
    this.changePeriod.emit({startTs: this.startDate.getTime(), endTs: this.endDate.getTime(), period: this.periodLabel});
  }

  private updatePeriodCustom(action) {
    const periodTs = moment(this.endDate).valueOf() - moment(this.startDate).valueOf();
    this.startDate = new Date(moment(this.startDate)[action](periodTs, 'millisecond').valueOf());
    if (this.isLastDate('millisecond', periodTs) && action === 'add') {
      this.endDate = new Date();
    } else {
      this.endDate = new Date(moment(this.endDate)[action](periodTs, 'millisecond').valueOf());
    }
    if (moment(this.endDate).valueOf() > moment().endOf('d').valueOf()) {
      this.endDate = new Date(moment().endOf('d').valueOf());
    }
    this.changePeriod.emit({startTs: this.startDate.getTime(), endTs: this.endDate.getTime(), period: this.periodLabel});
  }

  private isLastDate(period, count) {
    return moment(this.endDate).add(count, period).valueOf() > moment().valueOf();
  }

  private setDatepickerConfig() {
    const selectedPeriod = this.periodLabel.toLocaleLowerCase();
    const minMode: BsDatepickerViewMode = ['month', 'year'].includes(selectedPeriod) ? 
      (selectedPeriod as BsDatepickerViewMode) : 'day';

    this.bsConfig = combineLatest([this.themeService.getTheme(), this.datePickerOptions])
      .pipe( map(([theme, options]) => Object.assign(options, { containerClass: theme })));
    this.datePickerOptions.next({
      dateInputFormat: periodInputFormats[selectedPeriod],
      rangeInputFormat: 'DD MMM YYYY',
      adaptivePosition: true,
      selectWeekDateRange: true,
      selectFromOtherMonth: true,
      minMode
    });
  }

  private subscribeLanguageChange() {
    this.languageService.updateCalendars(this.translateService.currentLang,this.localeService);
    this.translateService.onLangChange.pipe(takeUntil(this.unsubscriber)).subscribe(event => {
      if (event) {
        this.languageService.updateCalendars(event.lang,this.localeService);
      }
    });
  }

}
